import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  apiUserCrmProfiles,
  IGridCrmProfileResponse,
  IGridCrmUsers,
} from 'services/user-crm-profiles';
import {
  contains,
  decoratorIsNotNullable,
  dynamicNamespace,
  equals,
  mergeFilters,
} from 'utils/dynamic';
import { API, parseErrorData } from 'utils/service';
import { IUserCrmProfile } from '__generated__/api';
import { AppAsyncThunkConfig } from '../index';
import {
  selectCrmUsersData,
  selectCrmUsersFilters,
  selectCrmUsersOrderBy,
  selectCrmUsersPagination,
} from './selectors';

const dynamic = dynamicNamespace<IUserCrmProfile>();

export const actionCrmUsersGetAllDynamic = createAsyncThunk<
  IGridCrmProfileResponse,
  { page?: number; take?: number } | void,
  AppAsyncThunkConfig
>(`CRM_USERS/getAllDynamic`, async (_, { getState }) => {
  try {
    const { search, userCrmProfilePermissionID, isActive } = selectCrmUsersFilters(getState());
    const { take: Take, skip: Skip } = selectCrmUsersPagination(getState());
    const { field, order } = selectCrmUsersOrderBy(getState());

    const params = {
      filter: mergeFilters(
        dynamic.makeFilter(['firstName', 'lastName'], search, decoratorIsNotNullable(contains)),
        dynamic.makeFilter(
          'userCrmProfilePermissionID',
          userCrmProfilePermissionID,
          decoratorIsNotNullable(equals),
        ),
        dynamic.makeFilter('isActive', isActive, decoratorIsNotNullable(equals)),
      ).join('&&'),
      select: dynamic.select(
        'id',
        'fullName',
        'userPhoto',
        'appIdentityUserID',
        'email',
        'isActive',
        'userCrmProfilePermissionID',
        'userCrmProfilePermission.title as position',
      ),
      orderBy: dynamic.orderBy(field, order) || 'userCrmProfilePermission.title,firstName,lastName',
      count: true,
      take: Take,
      skip: Skip,
    };
    const { data: result } = await API.api.userCrmProfileGetAllDynamicCreate(params);
    return result as IGridCrmProfileResponse;
  } catch (e: any) {
    throw parseErrorData(e);
  }
});

export const actionCrmUsersChangeItem = createAsyncThunk<
  void,
  Partial<IGridCrmUsers>,
  AppAsyncThunkConfig
>(`CRM_USERS/actionCrmUsersChangeItem`, async (data, { dispatch, getState }) => {
  try {
    const initData = selectCrmUsersData(getState());
    const dataOld = initData.find((item) => item.appIdentityUserID === data.appIdentityUserID);

    const appIdentityUserID = data.appIdentityUserID;
    if (!appIdentityUserID) {
      throw new Error('appIdentityUserID is required');
    }
    await dispatch(
      apiUserCrmProfiles.endpoints.patchUserCrmProfile.initiate({
        dataNew: { ...data, appIdentityUserID },
        dataOld,
      } as any),
    ).unwrap();
  } catch (e: any) {
    throw parseErrorData(e);
  }
});
